import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { MapPin, Save, UserRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import UserPanelShell from '../components/UserPanelShell';

const ProfilePage = () => {
  const { user, updateLocalUser } = useAuth();
  const [addresses, setAddresses] = useState(user?.addresses || []);
  const [profile, setProfile] = useState({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '', profileImage: user?.profileImage || '' });
  const [address, setAddress] = useState({ fullAddress: '', city: '', state: '', pincode: '', landmark: '', isDefault: true });
  const [message, setMessage] = useState('');

  if (!user) return <Navigate to="/login" replace />;

  const saveAddress = async (event) => {
    event.preventDefault();
    setMessage('');
    try {
      const response = await authApi.addAddress(address);
      setAddresses(response.data.data);
      setAddress({ fullAddress: '', city: '', state: '', pincode: '', landmark: '', isDefault: true });
      setMessage('Address saved.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not save address.');
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setMessage('');
    try {
      const response = await authApi.updateProfile(profile);
      const nextUser = response.data?.data;
      updateLocalUser(nextUser);
      setProfile({ name: nextUser?.name || '', email: nextUser?.email || '', phone: nextUser?.phone || '', profileImage: nextUser?.profileImage || '' });
      setMessage('Profile updated.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not update profile.');
    }
  };

  return (
    <UserPanelShell title="Profile Management" subtitle="Manage your personal details and saved delivery addresses.">
        <section className="space-y-6">
          <div className="bg-white border border-[#1F2A44]/10 p-6">
            <div className="flex items-center gap-2">
              <UserRound className="text-[#E8B530]" />
              <h2 className="font-serif text-2xl font-bold text-[#1F2A44]">Personal Details</h2>
            </div>

            <form onSubmit={saveProfile} className="grid md:grid-cols-2 gap-3 mt-5">
              <input required value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Full name" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
              <input required type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} placeholder="Email" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
              <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="Phone" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
              <input value={profile.profileImage} onChange={(e) => setProfile({ ...profile, profileImage: e.target.value })} placeholder="Profile image URL" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
              <button className="md:col-span-2 bg-[#1F2A44] text-[#FAF7F2] py-3 font-semibold uppercase tracking-wider text-sm flex items-center justify-center gap-2"><Save size={16} /> Save Profile</button>
            </form>
          </div>

          <div className="bg-white border border-[#1F2A44]/10 p-6">
          <div className="flex items-center gap-2">
            <MapPin className="text-[#E8B530]" />
            <h2 className="font-serif text-2xl font-bold text-[#1F2A44]">Delivery Addresses</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mt-5">
            {addresses.map((item) => (
              <div key={item._id || item.pincode} className="border border-[#1F2A44]/10 p-4 text-sm text-[#1F2A44]/75">
                <p className="font-semibold text-[#1F2A44]">{item.fullAddress}</p>
                <p>{item.city}, {item.state} - {item.pincode}</p>
                {item.landmark && <p>{item.landmark}</p>}
              </div>
            ))}
          </div>

          <form onSubmit={saveAddress} className="grid md:grid-cols-2 gap-3 mt-6">
            <input required value={address.fullAddress} onChange={(e) => setAddress({ ...address, fullAddress: e.target.value })} placeholder="Full address" className="md:col-span-2 border border-[#1F2A44]/20 px-4 py-3 text-sm" />
            <input required value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} placeholder="City" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
            <input required value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} placeholder="State" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
            <input required value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value })} placeholder="Pincode" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
            <input value={address.landmark} onChange={(e) => setAddress({ ...address, landmark: e.target.value })} placeholder="Landmark" className="border border-[#1F2A44]/20 px-4 py-3 text-sm" />
            <button className="md:col-span-2 bg-[#E8B530] text-[#1F2A44] py-3 font-semibold uppercase tracking-wider text-sm">Save Address</button>
            {message && <p className="md:col-span-2 text-sm text-[#1F2A44]/70">{message}</p>}
          </form>
          </div>
        </section>
    </UserPanelShell>
  );
};

export default ProfilePage;
